import React, { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";

interface GeofenceCircleProps {
  center: { lat: number; lng: number };
  radius: number;
}

/** Draws a geofence circle on the map using the Maps JS API directly. */
const GeofenceCircle: React.FC<GeofenceCircleProps> = ({ center, radius }) => {
  const map = useMap();
  const circleRef = useRef<google.maps.Circle | null>(null);
  const { lat, lng } = center;

  // Create the circle once, then update it in place when the centre/radius
  // *values* change. Keying on primitives (not the `center` object) means an
  // inline `center={{ lat, lng }}` — a fresh identity every render — no longer
  // tears the circle down and recreates it on each render.
  useEffect(() => {
    if (!map) return;

    if (!circleRef.current) {
      circleRef.current = new google.maps.Circle({
        map,
        center: { lat, lng },
        radius,
        strokeColor: "#1DD19C",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#1DD19C",
        fillOpacity: 0.15,
      });
    } else {
      circleRef.current.setCenter({ lat, lng });
      circleRef.current.setRadius(radius);
    }
  }, [map, radius, lat, lng]);

  // Remove the circle only on unmount — a GeofenceCircle is always tied to a
  // single Map instance, so `map` is stable for its lifetime.
  useEffect(
    () => (): void => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    },
    []
  );

  return null;
};

export default GeofenceCircle;
